import React from 'react';
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    IconButton,
    Box,
    Typography,
    Grid,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel
} from '@material-ui/core';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import ImageUpload from './ImageUpload';

const useStyles = makeStyles((theme) => ({
    formControl: {
        marginBottom: theme.spacing(2),
        width: '100%'
    },
    answerContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    uploadButton: {
        marginBottom: theme.spacing(2)
    }
}));

const QuestionTypeFields = ({ step, onUpdate }) => {
    const classes = useStyles();

    const handleChange = (field) => (event) => {
        onUpdate(field, event.target.value);
    };

    const handleAnswerChange = (index, value) => {
        const newAnswers = { ...step.answers };
        newAnswers[`answerSelection${String(index + 1).padStart(2, '0')}`] = value;
        onUpdate('answers', newAnswers);
    };

    const addAnswer = () => {
        const answersCount = Object.keys(step.answers || {}).length;
        const newAnswers = { ...step.answers };
        newAnswers[`answerSelection${String(answersCount + 1).padStart(2, '0')}`] = '';
        onUpdate('answers', newAnswers);
    };

    const removeAnswer = (key) => {
        const newAnswers = { ...step.answers };
        delete newAnswers[key];
        onUpdate('answers', newAnswers);
    };

    switch (step.questionType) {
        case 'video':
            return (
                <Box>
                    <TextField
                        label="Video Link"
                        value={step.videoLink || ''}
                        onChange={handleChange('videoLink')}
                        fullWidth
                        variant="outlined"
                        placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                        helperText="Paste the video URL here"
                    />
                </Box>
            );

        case 'download':
            return (
                <Box>
                    <ImageUpload
                        label="Link Download Report"
                        value={step.reportDownloadLink || ''}
                        onChange={handleChange('reportDownloadLink')}
                        uniqueId={`step-${step.id}-download`}
                    />
                </Box>
            );

        case 'openField':
            return (
                <Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Placeholder"
                                value={step.placeholder || ''}
                                onChange={handleChange('placeholder')}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel>Input Type</InputLabel>
                                <Select
                                    value={step.type || 'text'}
                                    onChange={handleChange('type')}
                                    label="Input Type"
                                >
                                    <MenuItem value="text">Text</MenuItem>
                                    <MenuItem value="number">Number</MenuItem>
                                    <MenuItem value="percentage">Percentage (%)</MenuItem>
                                    <MenuItem value="euros">Euro (€)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Correct Answer *"
                                value={step.validationField || ''}
                                onChange={handleChange('validationField')}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                    </Grid>
                </Box>
            );

        case 'selection':
            return (
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        Answers
                    </Typography>
                    {Object.entries(step.answers || {}).map(([key, value], index) => (
                        <Box key={key} className={classes.answerContainer}>
                            <TextField
                                label={`Answer ${index + 1}`}
                                value={value}
                                onChange={(e) => handleAnswerChange(index, e.target.value)}
                                variant="outlined"
                                fullWidth
                            />
                            <IconButton
                                size="small"
                                onClick={() => removeAnswer(key)}
                                color="secondary"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        startIcon={<AddIcon />}
                        onClick={addAnswer}
                        variant="contained"
                        style={{ 
                            backgroundColor: '#1976d2', 
                            color: '#ffffff',
                            marginBottom: '16px',
                            alignSelf: 'flex-start'
                        }}
                    >
                        Add Answer
                    </Button>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel>Correct Answer *</InputLabel>
                                <Select
                                    value={step.validationField || ''}
                                    onChange={handleChange('validationField')}
                                    label="Correct Answer *"
                                >
                                    <MenuItem value="">Select...</MenuItem>
                                    {Object.entries(step.answers || {}).map(([key, value]) => (
                                        <MenuItem key={key} value={key}>
                                            {value || key}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel>Randomize Answers</InputLabel>
                                <Select
                                    value={step.randomizeAnswer || false}
                                    onChange={handleChange('randomizeAnswer')}
                                    label="Randomize Answers"
                                >
                                    <MenuItem value={false}>No</MenuItem>
                                    <MenuItem value={true}>Yes</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            );

        case 'T/F':
            return (
                <Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl component="fieldset" fullWidth>
                                <FormLabel component="legend">Correct Answer *</FormLabel>
                                <RadioGroup
                                    value={step.validationField || ''}
                                    onChange={handleChange('validationField')}
                                    row
                                >
                                    <FormControlLabel
                                        value="TRUE"
                                        control={<Radio color="primary" />}
                                        label="TRUE"
                                    />
                                    <FormControlLabel
                                        value="FALSE"
                                        control={<Radio color="primary" />}
                                        label="FALSE"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel>Randomize Answers</InputLabel>
                                <Select
                                    value={step.randomizeAnswer || false}
                                    onChange={handleChange('randomizeAnswer')}
                                    label="Randomize Answers"
                                >
                                    <MenuItem value={false}>No</MenuItem>
                                    <MenuItem value={true}>Yes</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            );

        case 'uploadFile':
            return null;

        case 'imageSelection':
            return (
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        Multiple images answers
                    </Typography>
                    {Object.entries(step.answers || {}).map(([key, value], index) => (
                        <Box key={key} style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <Box style={{ flex: 1 }}>
                                <ImageUpload
                                    label={`URL Immagine ${index + 1} / Upload ${index + 1}`}
                                    value={value}
                                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                                    showClearButton={false}
                                    uniqueId={`step-${step.id}-img-${key}`}
                                />
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => removeAnswer(key)}
                                color="secondary"
                                style={{ marginTop: '8px' }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        startIcon={<AddIcon />}
                        onClick={addAnswer}
                        variant="contained"
                        style={{ 
                            backgroundColor: '#1976d2', 
                            color: '#ffffff',
                            marginBottom: '16px',
                            alignSelf: 'flex-start'
                        }}
                    >
                        Add Image
                    </Button>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel>Correct Answer *</InputLabel>
                                <Select
                                    value={step.validationField || ''}
                                    onChange={handleChange('validationField')}
                                    label="Correct Answer *"
                                >
                                    <MenuItem value="">Select...</MenuItem>
                                    {Object.entries(step.answers || {}).map(([key, value], index) => (
                                        <MenuItem key={key} value={key}>
                                            {`URL Immagine ${index + 1} / Upload ${index + 1}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel>Randomize Images</InputLabel>
                                <Select
                                    value={step.randomizeAnswer || false}
                                    onChange={handleChange('randomizeAnswer')}
                                    label="Randomize Images"
                                >
                                    <MenuItem value={false}>No</MenuItem>
                                    <MenuItem value={true}>Yes</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            );

        default:
            return (
                <Typography color="textSecondary">
                </Typography>
            );
    }
};

export default QuestionTypeFields;
